import React from "react";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";

import {
  AgentPropertiesPanel,
  TeamPropertiesPanel,
  ToolPropertiesPanel,
  InputPropertiesPanel,
  OutputPropertiesPanel,
} from "./panels";

const PlaygroundPropertiesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { workflow } = useWorkflowStore();
  const { selectedNodeId, isPropertiesPanelOpen, setSelectedNode, setPropertiesPanelOpen } =
    usePlaygroundStore();

  if (!isPropertiesPanelOpen || !selectedNodeId) {
    return null;
  }

  // Determine node type from workflow data
  const getNodeType = (): string | null => {
    if (!selectedNodeId) return null;
    if (selectedNodeId === "input") return "input";
    if (selectedNodeId === "output") return "output";
    if (workflow?.agents?.[selectedNodeId]) return "agent";
    if (workflow?.teams?.[selectedNodeId]) return "team";
    if (selectedNodeId.includes("_tool_")) return "tool";

    return null;
  };

  const nodeType = getNodeType();

  const handleClose = () => {
    setSelectedNode(null);
    setPropertiesPanelOpen(false);
  };

  // Extract tool info from node ID format: {agentId}_tool_{toolId}
  const getToolInfo = () => {
    if (!selectedNodeId.includes("_tool_")) return null;
    const parts = selectedNodeId.split("_tool_");

    return { toolId: parts[1], nodeId: selectedNodeId };
  };

  const renderPanel = () => {
    switch (nodeType) {
      case "agent":
        return <AgentPropertiesPanel agentId={selectedNodeId} />;
      case "team":
        return <TeamPropertiesPanel teamId={selectedNodeId} />;
      case "tool": {
        const toolInfo = getToolInfo();

        return toolInfo ? (
          <ToolPropertiesPanel toolId={toolInfo.toolId} toolNodeId={toolInfo.nodeId} />
        ) : null;
      }
      case "input":
        return <InputPropertiesPanel />;
      case "output":
        return <OutputPropertiesPanel />;
      default:
        return (
          <div className="p-4 text-center text-default-500">
            <Icon className="w-8 h-8 mx-auto mb-2" icon="solar:widget-2-outline" />
            <p className="text-sm">{t("Select a node to edit its properties")}</p>
          </div>
        );
    }
  };

  return (
    <div className="w-80 border-l border-default-200 bg-default-50/80 backdrop-blur-sm flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-default-200">
        <h3 className="text-sm font-semibold text-default-700">{t("Properties")}</h3>
        <Button
          isIconOnly
          aria-label={t("Close panel")}
          size="sm"
          variant="light"
          onPress={handleClose}
        >
          <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
        </Button>
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto p-4">{renderPanel()}</div>
    </div>
  );
};

export default PlaygroundPropertiesPanel;
